import {
  ApplicationPayload,
  IApplication,
  IManagedApplication,
  Project,
  ProjectPayload,
} from "src/domains/project/types";
import { useApi } from "src/platform/hooks/useApi";

// API endpoints
const PROJECT = "/api/v1/projects";
const APPLICATION = "/api/v1/applications";
const CACHE = "/api/v1/cache";
const MANAGED_APPLICATIONS = "/api/v1/managed_applications";

/**
 * Hook that provides project-related API functionality with standardized error handling
 */
export const useProjectService = () => {
  const api = useApi();

  /**
   * Get a project by alias
   * @param alias Project alias
   * @returns Project details or null if error
   */
  const getProject = async (alias: string): Promise<Project | null> => {
    return api.get<Project>(`${PROJECT}/${alias}`, {
      errorMessage: `Failed to get project: ${alias}`,
    });
  };

  /**
   * Get pending applications for a project
   * @param alias Project alias
   * @returns Array of pending applications or null if error
   */
  const getPendingApplications = async (
    alias: string,
  ): Promise<IApplication[] | null> => {
    return api.get<IApplication[]>(
      `${CACHE}?key=pending_applications&field=${alias}`,
      {
        errorMessage: `Failed to get pending applications for project: ${alias}`,
      },
    );
  };

  /**
   * Create a new application in a project
   * @param projectAlias Project alias
   * @param application Application payload
   * @returns Created application or null if error
   */
  const createApplication = async (
    projectAlias: string,
    application: ApplicationPayload,
  ): Promise<IApplication | null> => {
    return api.post<IApplication, ApplicationPayload>(
      `${APPLICATION}?project_alias=${projectAlias}`,
      application,
      {
        successMessage: `Application created successfully`,
        showSuccessNotification: true,
        errorMessage: "Failed to create application",
      },
    );
  };

  /**
   * Get all managed applications
   * @returns Array of managed applications or null if error
   */
  const getManagedApplications = async (): Promise<
    IManagedApplication[] | null
  > => {
    return api.get<IManagedApplication[]>(MANAGED_APPLICATIONS, {
      errorMessage: "Failed to get managed applications",
    });
  };

  /**
   * Update a project
   * @param alias Project alias
   * @param data Project update payload
   * @returns Updated project or null if error
   */
  const updateProject = async (
    alias: string,
    data: ProjectPayload,
  ): Promise<Project | null> => {
    return api.patch<Project, ProjectPayload>(`${PROJECT}/${alias}`, data, {
      successMessage: "Project updated successfully",
      showSuccessNotification: true,
      errorMessage: `Failed to update project: ${alias}`,
    });
  };

  /**
   * Delete a project (soft delete by default, hard delete with ?hard=true)
   * @param alias Project alias
   * @param hard If true, permanently delete (requires no active resources unless force)
   * @param force If true with hard, deprovision all resources before deleting
   * @returns true if successful, null if error
   */
  const deleteProject = async (
    alias: string,
    hard = false,
    force = false,
  ): Promise<boolean | null> => {
    const query = hard ? "?hard=true" + (force ? "&force=true" : "") : "";
    const result = await api.delete<void>(`${PROJECT}/${alias}${query}`, {
      successMessage: hard
        ? "Project permanently deleted"
        : "Project archived (soft delete)",
      showSuccessNotification: true,
      errorMessage: `Failed to delete project: ${alias}`,
    });

    return result !== null ? true : null;
  };

  /**
   * Restore a soft-deleted project
   * @param alias Project alias
   * @returns true if successful, null if error
   */
  const restoreProject = async (alias: string): Promise<boolean | null> => {
    const result = await api.post<void, Record<string, never>>(
      `${PROJECT}/${alias}/restore`,
      {},
      {
        successMessage: "Project restored successfully",
        showSuccessNotification: true,
        errorMessage: `Failed to restore project: ${alias}`,
      },
    );

    return result !== null ? true : null;
  };

  /**
   * Toggle delete protection on a project
   * @param alias Project alias
   * @param enabled If true, prevent deletion
   * @returns true if successful, null if error
   */
  const setDeleteProtection = async (
    alias: string,
    enabled: boolean,
  ): Promise<boolean | null> => {
    const result = await api.patch<void, { enabled: boolean }>(
      `${PROJECT}/${alias}/delete-protection`,
      { enabled },
      {
        successMessage: `Delete protection ${enabled ? "enabled" : "disabled"}`,
        showSuccessNotification: true,
        errorMessage: `Failed to set delete protection: ${alias}`,
      },
    );

    return result !== null ? true : null;
  };

  return {
    loading: api.loading,
    getProject,
    getPendingApplications,
    createApplication,
    getManagedApplications,
    updateProject,
    deleteProject,
    restoreProject,
    setDeleteProtection,
  };
};
